/**
 * Where each of an item's fields is kept, read from its links and the workspace's connected
 * systems (src/lib/itemFieldSources.ts). Null while loading.
 */
import { useEffect, useMemo, useState } from 'react';
import { fetchConnectorConfigsFromDB, getConnectorDefinition, type ConnectorConfigDB, type ConnectorType } from '@/lib/connectors';
import { resolveItemFieldSources, type ItemFieldSources } from '@/lib/itemFieldSources';
import type { ItemExternalLink } from './useItemExternalLinks';

export function useItemFieldSources(workspaceId: string | null | undefined, links: ItemExternalLink[] | null): ItemFieldSources | null {
  const [configs, setConfigs] = useState<ConnectorConfigDB[] | null>(null);
  const linked = (links?.length ?? 0) > 0;

  useEffect(() => {
    // Only an item linked somewhere needs the connections read.
    if (!workspaceId || !linked) {
      setConfigs([]);
      return;
    }
    let live = true;
    setConfigs(null);
    fetchConnectorConfigsFromDB(workspaceId)
      .then(({ data }) => live && setConfigs(data))
      .catch(() => live && setConfigs([]));
    return () => {
      live = false;
    };
  }, [workspaceId, linked]);

  return useMemo(() => {
    if (links === null || configs === null) return null;
    return resolveItemFieldSources(links, configs, (t) => getConnectorDefinition(t as ConnectorType)?.name ?? t);
  }, [links, configs]);
}
